import React, { useState } from 'react';
import './QuoteForm.css';

const QuoteForm = ({ isOpen, onClose, productName }) => {
  // Debug logging to see what productName actually is
  console.log('QuoteForm received productName:', productName, 'type:', typeof productName);
  
  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    phoneNumber: '',
    companyName: '',
    size: '',
    deliveryAddress: '',
    pincode: '',
    productName: (typeof productName === 'string' ? productName : '') || ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  // Test API health on component mount
  React.useEffect(() => {
    const testApiHealth = async () => {
      try {
        const healthUrl = window.location.hostname === 'localhost' 
          ? 'http://localhost:8080/extapi/health'
          : 'https://kolhapurnow.com/extapi/health';
        
        const response = await fetch(healthUrl);
        if (response.ok) {
          const health = await response.json();
          console.log('API Health:', health);
        }
      } catch (error) {
        console.warn('API health check failed:', error);
      }
    };

    if (isOpen) {
      testApiHealth();
    }
  }, [isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    // Validate required fields according to API documentation
    if (!formData.customerName || formData.customerName.length < 2 || formData.customerName.length > 100) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      console.error('Customer name must be between 2-100 characters');
      return;
    }

    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      console.error('Valid email address is required');
      return;
    }

    const companyName = formData.companyName || 'Individual Customer';
    if (companyName.length < 2 || companyName.length > 100) {
      setSubmitStatus('error');
      setIsSubmitting(false);
      console.error('Company name must be between 2-100 characters');
      return;
    }

    // Use localhost for development, production URL for live site
    const apiUrl = window.location.hostname === 'localhost' 
      ? 'http://localhost:8080/extapi/sendEmail'
      : 'https://kolhapurnow.com/extapi/sendEmail';

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customerName: formData.customerName,
          companyName: formData.companyName || 'Individual Customer',
          customerEmail: formData.email,
          customerPhone: formData.phoneNumber,
          orderType: 'quote',
          items: [{
            productName: formData.productName || 'General Inquiry',
            quantity: 1,
            unit: 'piece',
            specifications: formData.size ? `Size: ${formData.size}` : 'Please provide quote'
          }],
          address: formData.deliveryAddress && formData.pincode 
            ? `${formData.deliveryAddress}, ${formData.pincode}` 
            : formData.deliveryAddress || formData.pincode || '',
          specialRequirements: 'Quote request from website',
          preferredDeliveryDate: '',
          referenceNumber: ''
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('API Response:', result);
        
        // API returns { success: true, message: "...", timestamp: "..." }
        if (result.success === true) {
          setSubmitStatus('success');
          console.log('Quote sent successfully:', result.message);
          setTimeout(() => {
            setSubmitStatus('');
            setFormData({
              customerName: '',
              email: '',
              phoneNumber: '',
              companyName: '',
              size: '',
              deliveryAddress: '',
              pincode: '',
              productName: (typeof productName === 'string' ? productName : '') || ''
            });
            onClose();
          }, 3000);
        } else {
          console.error('Error in response:', result.message || result.error);
          setSubmitStatus('error');
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Error response:', errorData);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error sending quote request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="quote-modal-overlay" onClick={onClose}>
      <div className="quote-modal" onClick={(e) => e.stopPropagation()}>
        <div className="quote-header">
          <h2>Get Quote</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        {productName && typeof productName === 'string' && productName.length > 0 && (
          <div className="selected-product">
            <p><strong>Product:</strong> {productName}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="quote-form">
          <div className="form-group">
            <label htmlFor="customerName">Customer Name *</label>
            <input
              type="text"
              id="customerName"
              name="customerName"
              value={formData.customerName}
              onChange={handleChange}
              required
              placeholder="Enter your full name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email ID *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter your email address"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number *</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
              placeholder="Enter your phone number"
            />
          </div>

          <div className="form-group">
            <label htmlFor="companyName">Company Name</label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              placeholder="Enter company name (optional)"
            />
          </div>

          <div className="form-group">
            <label htmlFor="size">Size (Dia/Gauge)</label>
            <input
              type="text"
              id="size"
              name="size"
              value={formData.size}
              onChange={handleChange}
              placeholder="e.g., 18 gauge, 2.5mm dia, etc."
            />
          </div>

          <div className="form-group">
            <label htmlFor="deliveryAddress">Delivery Address *</label>
            <textarea
              id="deliveryAddress"
              name="deliveryAddress"
              value={formData.deliveryAddress}
              onChange={handleChange}
              required
              placeholder="Enter complete delivery address"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label htmlFor="pincode">Pincode *</label>
            <input
              type="text"
              id="pincode"
              name="pincode"
              value={formData.pincode}
              onChange={handleChange}
              required
              placeholder="Enter pincode"
              pattern="[0-9]{6}"
            />
          </div>

          {submitStatus === 'success' && (
            <div className="success-message">
              ✅ Quote request sent successfully! We'll contact you soon.
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="error-message">
              ❌ Failed to send request. Please try again or call us directly.
            </div>
          )}

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="btn-submit">
              {isSubmitting ? 'Sending...' : 'Send Quote Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuoteForm;